import { computed, ref, watch } from 'vue';
import { defineStore } from 'pinia';
import { useAuthStore } from '@/app/shared/stores/auth-store';
import useActivityTracker from './use-activity-tracker';

const useSessionTracker = defineStore('', () => {
  const authStore = useAuthStore();

  const displaySessionWarning = ref(false);
  const isTimeoutAllow = ref(true);
  const warningTimeInMs = ref(300000); // 5 minutes
  const earlyExpiryTimeInMs = ref(2000);
  const warningTimeoutHandle = ref();
  const sessionTimeoutHandle = ref();

  const expirationTime = computed(() => {
    return authStore.jwtExpirationTime;
  });

  const sessionHandler = () => {
    if (isTimeoutAllow.value && expirationTime.value) {
      clearWarningAndSessionTimeouts();

      const expirationDate = new Date(
        expirationTime.value * 1000 - earlyExpiryTimeInMs.value
      );

      const sessionTimeout = expirationDate.getTime() - Date.now();
      const warningTimeout =
        expirationDate.getTime() - Date.now() - warningTimeInMs.value;

      sessionTimeoutHandle.value = setTimeout(() => {
        sessionLogoutHandler();
      }, sessionTimeout);

      warningTimeoutHandle.value = setTimeout(async () => {
        const activityTracker = useActivityTracker();
        const wasRecentlyActive =
          Date.now() - activityTracker.lastActivityTime < warningTimeInMs.value;
        if (wasRecentlyActive) {
          if (authStore.shouldUseFormLogin) {
            await authStore.refreshFormAccessToken();
          } else if (authStore.shouldUseOktaLogin) {
            await authStore.refreshOktaAccessToken();
          }
        } else {
          if (authStore.preventExpiryWarning) sessionContinueHandler();
          else displaySessionWarning.value = true;
        }
      }, warningTimeout);
    }
  };

  const clearWarningAndSessionTimeouts = () => {
    warningTimeoutHandle.value && clearTimeout(warningTimeoutHandle.value);
    sessionTimeoutHandle.value && clearTimeout(sessionTimeoutHandle.value);
  };

  const sessionContinueHandler = async () => {
    displaySessionWarning.value = false;
    clearWarningAndSessionTimeouts();
    if (authStore.shouldUseFormLogin) {
      await authStore.refreshFormAccessToken();
    } else if (authStore.shouldUseOktaLogin) {
      await authStore.refreshOktaAccessToken();
    }
  };

  const sessionLogoutHandler = async () => {
    displaySessionWarning.value = false;
    clearWarningAndSessionTimeouts();
    authStore.authLogout();
  };

  const toggleTimeout = () => {
    isTimeoutAllow.value = !isTimeoutAllow.value;
  };

  watch(expirationTime, () => {
    if (expirationTime.value) {
      sessionHandler();
    } else {
      clearWarningAndSessionTimeouts();
    }
  });

  return {
    displaySessionWarning,
    expirationTime,
    warningTimeInMs,
    sessionHandler,
    sessionContinueHandler,
    sessionLogoutHandler,
    toggleTimeout,
  };
});

export default useSessionTracker;
